import { generatePath, Redirect, useParams } from 'react-router-dom';
import {
  ActionableNotification,
  InlineLoading,
  InlineNotification,
} from '@carbon/react';
import routes from '~/routes';
import { useEvaluationWorkspaces } from './hooks';
import { eval3EvalPath, eval3WorkspacePath } from './utils';
import { Eval3NewPage } from './page';
import styles from './eval3-landing-resolver.module.scss';

/**
 * Redirects a bare `/packages/:id` to `/packages/:id/eval3` client-side — replacing the
 * old full-page `window.location` bounce so entering a package from anywhere in the app
 * never blank-reloads. `<Redirect>` replaces the history entry (Back returns to the
 * originating page); the `/eval3` route then hands off to `Eval3LandingResolver`.
 *
 * @return {JSX.Element} A client redirect to the package's Eval 3 landing.
 */
export function PackageEval3Redirect(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  return (
    <Redirect
      to={generatePath(routes.packageEval3NewPath, { packageId: id })}
    />
  );
}

/**
 * Resolves the bare `/packages/:id/eval3` landing to the latest eval CLIENT-SIDE, so
 * in-app navigation lands on an eval with no full-page reload. This mirrors the server
 * resolver (`Packages::Eval3Controller#resolve_eval3_new_route`), which still handles
 * cold loads / direct links / refresh / 404s — a dual model. Reads the workspace list
 * (already ordered `by_last_activity DESC`) and redirects to the most-recently-active
 * workspace's latest calc (`latestCalculationId`, server-computed — no second fetch);
 * lands on the workspace browse view when it has no calc, and the package-landing page
 * when the package has no workspaces. Never mounts the heavy `Eval3NewContent` — that
 * page mounts only on a canonical workspace/eval URL, keeping its "trust canonical
 * identity" contract intact.
 *
 * @return {JSX.Element} A loading shell, an error notification, a `<Redirect>`, or the package landing.
 */
export function Eval3LandingResolver(): JSX.Element {
  const { packageId } = useParams<{ packageId: string }>();
  const parsedPackageId = Number(packageId);

  if (!Number.isInteger(parsedPackageId) || parsedPackageId <= 0) {
    return (
      <InlineNotification
        kind="error"
        lowContrast
        hideCloseButton
        title="Invalid evaluation URL"
        subtitle="The package identifier is missing or not a number."
      />
    );
  }

  return <ResolveEval3Landing packageId={parsedPackageId} />;
}

/**
 * Inner resolver — split out so the workspaces query only runs for a valid `packageId`
 * (hooks must be called unconditionally, so the id guard lives in the outer component).
 *
 * @param {{ packageId: number }} props - The validated package id.
 * @return {JSX.Element} A loading shell, an error notification, a `<Redirect>`, or the package landing.
 */
function ResolveEval3Landing({
  packageId,
}: {
  packageId: number;
}): JSX.Element {
  const { data, isPending, error, refetch } =
    useEvaluationWorkspaces(packageId);

  if (isPending) {
    return (
      <div className={styles.body}>
        <InlineLoading description="Loading Eval 3.0…" />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.body}>
        <ActionableNotification
          inline
          kind="error"
          lowContrast
          hideCloseButton
          title="Couldn't load Eval 3.0"
          subtitle={error.message || 'Please try again.'}
          actionButtonLabel="Retry"
          onActionButtonClick={() => refetch()}
        />
      </div>
    );
  }

  const workspaces = data?.workspaces ?? [];

  // No workspaces → the package-landing page, matching the server returning nil from
  // `redirect_to_auto_selected_eval`. Rendered on the same bare `/eval3` route, so
  // `Eval3NewPage`'s `:packageId` param resolves correctly (package mode).
  if (workspaces.length === 0) {
    return <Eval3NewPage />;
  }

  // `workspaces[0]` is the most-recently-active (server `by_last_activity` order).
  const ws = workspaces[0];
  const target =
    ws.latestCalculationId !== null
      ? eval3EvalPath(packageId, ws.id, ws.latestCalculationId)
      : eval3WorkspacePath(packageId, ws.id);

  return <Redirect to={target} />;
}
